import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { unwrappedMetricLookupMapAtom } from '@/atoms/queries/metric';
import { playerAggsCountAtom } from '@/atoms/queries/player/playerAggData';
import {
  playerSummaryAggregatesAtom,
  playerSummaryPositionMinutesAtom,
  playerSummaryPositionsAtom,
} from '@/atoms/queries/player/playerAiSummaryFilters';
import { playerPositionsSettledAtom } from '@/atoms/queries/player/playerPositions';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags/usePhysicalMetricsFeature';
import { PHYSICAL_SOURCE } from '@/types/metric';
import { isNonEmptyArray } from '@/utils/predicates';
import type { MetricDefinition, MetricLookupMap } from '@/types/metric';
import type { FilteredMetric, PositionMinutes } from '@statsbomb/kitbag-ai-summary';

const unwrappedPlayerSummaryPositionsAtom = unwrap(playerSummaryPositionsAtom, () => undefined);
const unwrappedPlayerSummaryPositionMinutesAtom = unwrap(playerSummaryPositionMinutesAtom, () => undefined);
const unwrappedPlayerSummaryAggregatesAtom = unwrap(playerSummaryAggregatesAtom, () => undefined);

// Bookkeeping field on the aggregates payload — surfaced separately as minutesPlayed, never a metric.
const MINUTES_PLAYED_KEY = 'minutes_played';

export const INCOMPLETE_DATA_MESSAGE = 'Player summary data incomplete: missing aggregates, positions, or cohort size';
export const NO_METRICS_MESSAGE = 'Player summary data incomplete: no metrics found in radar data';

export type PlayerSummaryData = {
  metrics: FilteredMetric[];
  minutesPlayed: number;
  primaryPosition: string;
  positions: [string, ...string[]];
  positionMinutes: PositionMinutes[];
  cohortSize?: number;
  // Metric keys (both raw and normalised forms) that must not reach the AI agent for this user, so
  // other payload fields built from catalog keys (e.g. the profile's metrics) can be filtered alike.
  hiddenMetricKeys: ReadonlySet<string>;
};

/**
 * A per-90 metric has a distinct normalised key (e.g. `np_xg` → `np_xg_per_90`).
 * Ratio/percentage metrics are self-normalised (`aerial_ratio` → `aerial_ratio`), so
 * `keyNormalised === key`. No suffix matching or `percentage` flag needed.
 */
export const isMetricPer90 = (metric: MetricDefinition): boolean => metric.keyNormalised !== metric.key;

/**
 * Maps raw aggregate keys to their catalog metric, keeping only catalog-recognised, numeric
 * entries. A metric is registered under both its raw key and its keyNormalised (see
 * metricLookupMapAtom), so if a future API response ever included both forms of the same metric,
 * this dedupes on the metric's canonical key — first form encountered wins.
 * Keys in `hiddenMetricKeys` are dropped before matching.
 * Extracted as a pure function so this core matching logic can be unit tested without the
 * Jotai/hook harness.
 */
export function buildCatalogMetrics(
  aggregates: Record<string, number>,
  metricLookupMap: MetricLookupMap,
  per90Label: string,
  hiddenMetricKeys: ReadonlySet<string>,
): PlayerSummaryData['metrics'] {
  const seenMetricKeys = new Set<string>();
  return Object.entries(aggregates)
    .filter(([key, value]) => typeof value === 'number' && key !== MINUTES_PLAYED_KEY && !hiddenMetricKeys.has(key))
    .flatMap(([key, value]) => {
      const metric = metricLookupMap.get(key);
      if (!metric || seenMetricKeys.has(metric.key)) return [];
      seenMetricKeys.add(metric.key);
      return [
        {
          metricKey: metric.key,
          per90Value: String(value),
          ...(isMetricPer90(metric) ? { valueLabel: per90Label } : {}),
        },
      ];
    });
}

// Physical metrics are dev-team gated, so they're withheld from the AI agent for everyone else. The
// lookup map holds both key forms of each metric, so the returned set covers either form.
const getHiddenMetricKeys = (metricLookupMap: MetricLookupMap, canSeePhysical: boolean): ReadonlySet<string> =>
  canSeePhysical
    ? new Set()
    : new Set([...metricLookupMap].filter(([, metric]) => metric.source === PHYSICAL_SOURCE).map(([key]) => key));

/**
 * Reads normalised aggregates from the radar data atom and returns a PlayerData-shaped
 * object for the AI summary component. Every catalog-recognised metric is included —
 * both per-90 metrics and ratio/percentage metrics — keyed off the metric catalog rather
 * than a key-name suffix, so no metric type is silently dropped (EPT-33791).
 * Reuses the radar's existing fetch — no additional API call needed.
 */
export const usePlayerFilteredSummaryData = (): PlayerSummaryData | undefined => {
  const { t } = useTranslation('aiSummary');
  const aggregates = useAtomValue(unwrappedPlayerSummaryAggregatesAtom);
  const cohortSize = useAtomValue(playerAggsCountAtom);
  const positions = useAtomValue(unwrappedPlayerSummaryPositionsAtom);
  const positionMinutes = useAtomValue(unwrappedPlayerSummaryPositionMinutesAtom);
  const filtersSettled = useAtomValue(playerPositionsSettledAtom);
  // Typically already resolved: the scout table renders alongside this panel and depends on the
  // same catalog. Still guarded below for first-visit deep links or a cache-evicted catalog.
  const metricLookupMap = useAtomValue(unwrappedMetricLookupMapAtom);
  const canSeePhysical = usePhysicalMetricsFeature();

  if (
    aggregates === undefined ||
    positions === undefined ||
    positionMinutes === undefined ||
    cohortSize === undefined ||
    metricLookupMap === undefined
  )
    return undefined;

  // During a player switch, the games list (and therefore the radar/positions queries keyed on it)
  // briefly lags behind the new player while still reporting resolved (but stale) data — see
  // playerPositionsSettledAtom. Treat that window as loading rather than an invariant violation, or
  // the resulting empty aggregates get thrown as a permanent error before the real data arrives.
  if (!filtersSettled) return undefined;

  const hasAggregates = Object.keys(aggregates).length > 0;

  if (!hasAggregates || !isNonEmptyArray(positions) || (cohortSize !== null && !(cohortSize > 0)))
    throw new Error(INCOMPLETE_DATA_MESSAGE);

  const minutesPlayedRaw = aggregates[MINUTES_PLAYED_KEY];
  const minutesPlayed = typeof minutesPlayedRaw === 'number' ? minutesPlayedRaw : 0;

  const hiddenMetricKeys = getHiddenMetricKeys(metricLookupMap, canSeePhysical);
  const metrics = buildCatalogMetrics(aggregates, metricLookupMap, t('metricPopover.per90Label'), hiddenMetricKeys);

  if (!metrics.length) throw new Error(NO_METRICS_MESSAGE);

  return {
    metrics,
    hiddenMetricKeys,
    minutesPlayed,
    primaryPosition: positions[0],
    positions,
    positionMinutes,
    ...(typeof cohortSize === 'number' ? { cohortSize } : {}),
  };
};
